import { Box, Divider, Grid, List, ListItem } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import React, { Dispatch, SetStateAction, useMemo } from 'react';

import DateFnsUtils from '@date-io/date-fns';
import {
  faCommentQuestion,
  faQuestion,
  faThumbsDown,
  faThumbsUp,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HeadCellImplementationProps } from '../../../../../components/muiLegacy/Tables/KeywordTable/HeadCells/models';
import HeadCell from '../../../../../components/muiLegacy/Tables/Shared/HeadCell';
import HeadCellStandardOptions from '../../../../../components/muiLegacy/Tables/Shared/HeadCellStandardOptions';
import VoxelChip from '../../../../../components/voxel/Chips/VoxelChip';
import { ContentTaskStatusGq } from '../../../../../graphql/projects/models/ContentTaskStatusGq';
import { CommandGq } from '../../../../../graphql/projects/queries/contentPlan/useGetContentTasksGq';
import VoxelMenuItemContainer from '../../../../../components/voxel/PopoverMenu/VoxelMenuItemContainer';

export interface HeadCellApprovedProps extends HeadCellImplementationProps {
  command: CommandGq;
  setCommand: Dispatch<SetStateAction<CommandGq>>;
  minState?: string;
  maxState?: string;
  setMinState?: (state?: string) => void;
  setMaxState?: (state?: string) => void;
}

const HeadCellApproved: React.FC<HeadCellApprovedProps> = ({
  command,
  setCommand,
  minState,
  maxState,
  setMinState,
  setMaxState,
  basicConfig,
  basicState,
}) => {
  const enumList = Object.values(ContentTaskStatusGq);

  const approvedList = useMemo(() => {
    if (!command.filtering.approvedStatus.length) {
      return enumList?.map((a) => ({
        approved: a,
        selected: false,
      }));
    } else {
      let selectedApprovedStatus = enumList
        ?.map((a, index) => ({
          index: index,
          approved: a,
          selected: true,
        }))
        .filter((a) => command.filtering.approvedStatus.includes(a.approved));
      let unselectedApprovedStatus = enumList
        ?.map((a, index) => ({
          index: index,
          approved: a,
          selected: false,
        }))
        .filter((a) => !command.filtering.approvedStatus.includes(a.approved));

      return [...selectedApprovedStatus, ...unselectedApprovedStatus].sort(
        (a, b) => a.index - b.index
      );
    }
  }, [command.filtering.approvedStatus]);

  const handleFilterChange = (value: ContentTaskStatusGq, selected: boolean) => {
    setCommand((prev) => {
      const approvedStatus = prev.filtering.approvedStatus.filter((a) => !(a === value));
      selected && approvedStatus.push(value);
      return {
        ...prev,
        filtering: { ...prev.filtering, approvedStatus },
      };
    });
  };

  return (
    <HeadCell
      hasFilter={!!command.filtering.approvedStatus.length}
      align={'center'}
      basicConfig={basicConfig}
      basicState={basicState}
    >
      <VoxelMenuItemContainer size="md">
        <HeadCellStandardOptions basicConfig={basicConfig} basicState={basicState} />
        <List>
          {approvedList.map((a, index) => (
            <ListItem
              selected={a.selected}
              key={index}
              button
              onClick={() => handleFilterChange(a.approved, !a.selected)}
            >
              <>
                {a.approved === ContentTaskStatusGq.UNDECIDED && (
                  <VoxelChip
                    title={'Undecided'}
                    icon={<FontAwesomeIcon icon={faQuestion} />}
                    color="neutral"
                  />
                )}
                {a.approved === ContentTaskStatusGq.APPROVED && (
                  <VoxelChip
                    title={'Approved'}
                    icon={<FontAwesomeIcon icon={faThumbsUp} />}
                    color="success"
                  />
                )}
                {a.approved === ContentTaskStatusGq.PROPOSAL && (
                  <VoxelChip
                    title={'Proposal'}
                    icon={<FontAwesomeIcon icon={faCommentQuestion} />}
                    color="info"
                  />
                )}
                {a.approved === ContentTaskStatusGq.DISAPPROVED && (
                  <VoxelChip
                    title={'Disapproved'}
                    icon={<FontAwesomeIcon icon={faThumbsDown} />}
                    color="error"
                  />
                )}
              </>
            </ListItem>
          ))}
        </List>
        <Divider />
        <Box p={1} py={1.5}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container direction={'row'} spacing={2}>
              <Grid item xs={6}>
                <KeyboardDatePicker
                  disableToolbar
                  fullWidth
                  margin="dense"
                  variant="inline"
                  disableFuture
                  format="yyyy-MM-dd"
                  value={minState ?? ''}
                  onChange={(date, value) => setMinState && setMinState(date?.toISOString())}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                    size: 'small',
                  }}
                  label="Start date"
                  InputLabelProps={{ shrink: true }}
                  helperText={null}
                  error={false}
                />
              </Grid>
              <Grid item xs={6}>
                <KeyboardDatePicker
                  disableToolbar
                  fullWidth
                  margin="dense"
                  variant="inline"
                  disableFuture
                  format="yyyy-MM-dd"
                  value={maxState ?? ''}
                  onChange={(date, value) => setMaxState && setMaxState(date?.toISOString())}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                    size: 'small',
                  }}
                  label="End date"
                  InputLabelProps={{ shrink: true }}
                  helperText={null}
                  error={false}
                />
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </Box>
      </VoxelMenuItemContainer>
    </HeadCell>
  );
};

export default HeadCellApproved;
